/**
  * Dropdown component Events and Methods
  *
  * @param {element} element - Dropdown element
  */
 class Dropdown {
  /** Instances that use outside-click closing (one shared document listener). */
  static _outsideClickInstances = [];
  static _documentCaptureBound = false;

  static _onDocumentClickCapture = (event) => {
    const list = Dropdown._outsideClickInstances;
    for (let i = 0; i < list.length; i++) {
      const inst = list[i];
      if (!inst.dropdown.classList.contains('open')) continue;
      if (!inst.dropdownMenu.contains(event.target)) {
        inst.dropdownHide(inst.dropdown);
      }
    }
  };

  static _ensureDocumentCapture() {
    if (Dropdown._documentCaptureBound) return;
    document.addEventListener('click', Dropdown._onDocumentClickCapture, true);
    Dropdown._documentCaptureBound = true;
  }

  constructor(element) {
    this.dropdown = element;
    this.dropdownMenu = this.dropdown ? this.dropdown.querySelector('.dropdown-menu') : null;
    this.toggleCTA = element.querySelector('.dropdown-toggle');

    if (this.dropdownMenu) {
      Dropdown._outsideClickInstances.push(this);
      Dropdown._ensureDocumentCapture();
    }

    this.init();
  }

  init() {
    const toggle = this.toggleCTA;
    const dd = this.dropdown;
    if (!toggle) return;

    toggle.addEventListener('touchend', (evt) => {
      evt.preventDefault();
      this.toggleDropdown();
    });

    toggle.addEventListener('mouseenter', () => {
      this.dropdownShow(dd);
      this.childOnFocusChange(dd);
    });

    dd.addEventListener('mouseleave', () => {
      this.dropdownHide(dd);
    });

    dd.addEventListener('focusout', (evt) => {
      if (!dd.contains(evt.relatedTarget) && dd.classList.contains('open')) {
        this.dropdownHide(dd);
      }
    });

    this.toggleCTA.addEventListener('keydown', (evt) => {
      const key = evt.key;
      if (key === ' ') {
        evt.preventDefault();
        this.toggleDropdown();
      } else if (key === 'ArrowDown') {
        evt.preventDefault();
        if (!this.dropdown.classList.contains('open')) {
          this.dropdownShow(this.dropdown);
        }
        this.focusFirstMenuItem();
      } else if (key === 'Escape' && this.dropdown.classList.contains('open')) {
        evt.preventDefault();
        this.dropdownHide(this.dropdown);
        this.toggleCTA.focus();
      }
    });

    if (this.dropdownMenu) {
      this.dropdownMenu.addEventListener('keydown', (evt) => {
        const key = evt.key;
        if (key === 'Escape') {
          evt.preventDefault();
          this.dropdownHide(this.dropdown);
          this.toggleCTA.focus();
        } else if (key === 'ArrowDown') {
          evt.preventDefault();
          this.focusNextMenuItem(evt.target);
        } else if (key === 'ArrowUp') {
          evt.preventDefault();
          this.focusPrevMenuItem(evt.target);
        }
      });
    }

    this.bindEvents();
  }

  bindEvents() {
    this.bindCloseButton();
  }

  bindCloseButton() {
    const closeBtn = this.dropdown.querySelector('.close-submenu');
    if (closeBtn) {
      closeBtn.addEventListener('click', (evt) => {
        evt.preventDefault();
        this.dropdownToggle(this.dropdown);
      });
    }
  }

  toggleDropdown() {
    this.dropdownToggle(this.dropdown);
  }

  dropdownToggle(dropdownParent) {
    setTimeout(() => {
      dropdownParent.classList.contains('open')
        ? this.dropdownHide(dropdownParent)
        : this.dropdownShow(dropdownParent);
    }, 50);
  }

  dropdownShow(dropdownParent) {
    if (!dropdownParent) {
      return;
    }
    this.toggleCTA.setAttribute('aria-expanded', true);
    dropdownParent.classList.add('open');
  }

  dropdownHide(dropdownParent) {
    if (!dropdownParent) return;

    this.toggleCTA.setAttribute('aria-expanded', false);
    dropdownParent.classList.remove('open');

    const childDropdowns = dropdownParent.querySelectorAll('.dropdown.open');
    if (childDropdowns.length > 0) {
      childDropdowns.forEach((child) => {
        child.classList.remove('open');
        child.querySelector('.dropdown-toggle').setAttribute('aria-expanded', false);
      });
    }
  }

  childOnFocusChange(dropdownParent) {
    const openDropdowns = dropdownParent.parentElement.querySelectorAll('.dropdown.open');
    for (let i = 0; i < openDropdowns.length; i++) {
      const ele = openDropdowns[i];
      if (ele !== dropdownParent) {
        this.dropdownHide(ele);
      }
    }
  }

  focusFirstMenuItem() {
    if (!this.dropdownMenu) return;
    const items = this.dropdownMenu.querySelectorAll('a, button');
    if (items.length) items[0].focus();
  }

  focusNextMenuItem(current) {
    if (!this.dropdownMenu) return;
    const items = Array.from(this.dropdownMenu.querySelectorAll('a, button'));
    const index = items.indexOf(current.closest('a, button') || current);
    if (index >= 0 && index < items.length - 1) items[index + 1].focus();
    else if (items.length) items[0].focus();
  }

  focusPrevMenuItem(current) {
    if (!this.dropdownMenu) return;
    const items = Array.from(this.dropdownMenu.querySelectorAll('a, button'));
    const index = items.indexOf(current.closest('a, button') || current);
    if (index > 0) items[index - 1].focus();
    else if (items.length) items[items.length - 1].focus();
  }
}
const allDropdowns = document.querySelectorAll('[is="drop-down"]');
allDropdowns.forEach((ele) => {
  new Dropdown(ele);
});

/**
* background Overlay
*/
class SiteOverlay extends HTMLElement{
  static _overlayEl = undefined;
  static _getOverlayEl() {
    if (SiteOverlay._overlayEl === undefined) {
      SiteOverlay._overlayEl = document.querySelector('#site-overlay');
    }
    return SiteOverlay._overlayEl;
  }

  constructor() {
    super();
    this._onOverlayClick = this.onClick.bind(this);
    this.bindEvents();
  }

  /**
   * bind Click and Touchend event to this custom component
   */
  bindEvents(){
    this.addEventListener('click', this._onOverlayClick);
    this.addEventListener('touchend', this._onOverlayClick);
  }

  /**
   * Click event of Site overlay
   */
  onClick(){
    const closeSearch = document.querySelector('search-modal')?.classList.contains("open__modal");
    if(closeSearch) document.querySelector('.search-modal__close-button')?.dispatchEvent(new Event('click'));
  
    this.hideOverlay();
  }

  /**
   * Show Overlay
   */
   showOverlay(){
    const el = SiteOverlay._getOverlayEl();
    if (!el) return;
    el.classList.add('overlay--body');
    el.setAttribute('aria-hidden', false);
    document.body.classList.add('scroll-fixed');
  }

  /**
   * Hide overlay
   */
  hideOverlay(){
    const openDrawers = document.querySelectorAll('custom-drawer.open');
    for (let i = 0; i < openDrawers.length; i++) {
      openDrawers[i].closeDrawer();
    }

    const el = SiteOverlay._getOverlayEl();
    if (el) {
      el.classList.remove('overlay--body');
      el.setAttribute('aria-hidden', true);
    }
    document.body.classList.remove('scroll-fixed');
    document.querySelector(".product-quickview-drawer")?.classList.remove("open");
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onOverlayClick);
    this.removeEventListener('touchend', this._onOverlayClick);
  }
}
customElements.define("site-overlay", SiteOverlay, {
  once: true
});

/**
* Custom Video
*/
class CustomVideo extends HTMLElement {
  /** One lookup per page for the shared video modal (was N× getElementById). */
  static _sharedVideoModal = undefined;
  static _getSharedVideoModal() {
    if (CustomVideo._sharedVideoModal === undefined) {
      CustomVideo._sharedVideoModal = document.getElementById('PopupModal-video') || null;
    }
    return CustomVideo._sharedVideoModal;
  }

  static _videoIdSeq = 0;

  constructor() {
    super();

    this.type = this.getAttribute('data-video-type');
    this.videoURL = this.getAttribute('data-video-id');
    this.placementType = this.getAttribute('data-placement');
    this.videoStatus = 'empty';

    this.container = this.closest('.custom-video-container');
    this.contentSection = this.querySelector('.content-section');
    this.videoSection = this.querySelector('.video-section');
    this.triggerVideo = this.container && this.container.querySelector('[data-trigger-video]');

    this.videoModal = null;
    if (this.placementType === 'popup') {
      this.videoModal = CustomVideo._getSharedVideoModal();
      const modalHost = this.videoModal && this.videoModal.querySelector('.video-container');
      if (modalHost) this.videoSection = modalHost;
    }

    this._onToggleView = this.toggleView.bind(this);
  }

  connectedCallback() {
    this.bindEvents();
  }

  disconnectedCallback() {
    if (this.triggerVideo && this._onToggleView) {
      this.triggerVideo.removeEventListener('click', this._onToggleView);
    }
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
      this.scrollObserver = null;
    }
    if (this._posterObserver) {
      this._posterObserver.disconnect();
      this._posterObserver = null;
    }
    if (this._autoplayObserver) {
      this._autoplayObserver.disconnect();
      this._autoplayObserver = null;
    }
  }

  /**
   * Binding events on the video button
   */
  bindEvents(){
    const posterEl = this.querySelector('[data-postervideo]');
    if (posterEl) this.managePosterVideos(posterEl);
    if (this.triggerVideo) this.triggerVideo.addEventListener('click', this._onToggleView);
    if (this.dataset.autoplay == 'true') this.autoplayOnInit();
    // if(this.placementType == 'grid') this.addEventListener('focusout', this.focusOut.bind(this));
  }

  /**
   * Focus Out Event to close drawer
   *
   * @param {event} Event instance
   */
  focusOut(_event){
    if(this.container.classList.contains('playing--video')) this.toggleView();
  }

  /**
   * Toggle of video modal
   */
  toggleView(event){
    if(event) event.preventDefault();
    const container = this.container || this.closest('.custom-video-container');
    const isOpen = container && container.classList.contains('playing--video');
    if (isOpen) {
      this.hideContent();
    } else {
      this.showContent();
    }
  }

  /**
   * Show panel content
   */
  showContent() {
    const videoSection = this.videoSection || this.querySelector('.video-section');
    const container = this.container || this.closest('.custom-video-container');
    if (!videoSection || !container) return;

    if (this.videoStatus === 'empty' || this.placementType === 'popup') {
      const videoElement = this._buildVideo();
      if (!videoElement) return;
      CustomVideo._videoIdSeq += 1;
      videoElement.id = 'custom-video' + CustomVideo._videoIdSeq;
      videoSection.appendChild(videoElement);
      this.videoStatus = 'loaded';

      setTimeout(() => {
        (this.placementType === 'popup') ? this.openModal(this.videoModal || CustomVideo._getSharedVideoModal(), this.triggerVideo) : container.classList.add('playing--video');
        videoSection.setAttribute('aria-hidden', 'false');
        this._playVideo(videoElement);
        if (this.dataset.controls == 'true') this.pauseOnVisibilityChange(videoElement);
      }, 500);
    } else {
      const videoElement = this.querySelector('.video-section video') || this.querySelector('.video-section iframe');
      if (videoElement) {
        container.classList.add('playing--video');
        this._playVideo(videoElement);
      }
    }
  }

  /**
   * Hide panel content
   */
  hideContent() {
    const container = this.container || this.closest('.custom-video-container');
    const videoSection = this.videoSection || this.querySelector('.video-section');
    if (container) container.classList.remove('playing--video');
    if (videoSection) videoSection.setAttribute('aria-hidden', 'true');
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
      this.scrollObserver = null;
    }
    const videoElement = this.querySelector('.video-section video') || this.querySelector('.video-section iframe');
    if (videoElement) {
      this._pauseVideo(videoElement);
    }
  }

  _playVideo(video){
    if(this.type == 'local'){
      video.play();
    }else if(this.type == 'youtube'){
      video.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
    }
  }

  _pauseVideo(video) {
    if (this.type === 'local') {
      video.pause();
    } else if (this.type === 'youtube') {
      video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
    }
  }

  /**
   * 
   * Create the video html element
   */
  _buildVideo(){
    let video;

    if(this.type == 'local'){
      video = document.createElement('video');
      video.src = this.videoURL;
      if(this.dataset.controls == 'true') video.controls = this.dataset.controls;
      if(this.dataset.muted == 'true') video.muted = this.dataset.muted;
      if(this.dataset.autoplay == 'true') video.autoplay = this.dataset.autoplay;
      if(this.dataset.loop == 'true') video.loop = this.dataset.loop;
      video.playsInline = true;
      const track = document.createElement('track');
      track.kind = 'captions';
      track.label = 'Captions';
      track.default = true;
      video.appendChild(track);
    }else if(this.type == 'youtube'){
      const videoID = this.videoURL.replace('https://www.youtube.com/watch?v=', '');
      video = document.createElement( "iframe" );
      video.setAttribute( "allowfullscreen", "" );
      video.setAttribute( "frameborder", "0" );
      video.setAttribute( "allow", "autoplay");
      video.setAttribute( "title", "YouTube video player" );
      video.setAttribute( "src", `https://www.youtube.com/embed/${videoID}?rel=0&showinfo=0&autoplay=${this.dataset.autoplay == 'true' ? 1 : 0}&muted=${this.dataset.muted == 'true' ? 1 : 0}&controls=${this.dataset.controls == 'true' ? 1 : 0}&loop=${this.dataset.loop == 'true' ? 1 : 0}&enablejsapi=1&origin=${window.origin}` );
      // &playlist=${videoID}
    }
    return video;
  }

  /**
   * Pause video on scroll when out of viewport
   *
  */
  pauseOnVisibilityChange(videoEle){
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
    }
    this.scrollObserver = new IntersectionObserver((entries) => {
      if (videoEle == undefined || videoEle.contentWindow == null) {
        this.scrollObserver.disconnect();
        return;
      }

      if (!entries[0].isIntersecting) {
        this._pauseVideo(videoEle);
      }
    });
    this.scrollObserver.observe(videoEle);
  }

  /**
   * Poster Video Autoplay
   *
  */
  managePosterVideos(posterVideo){
    if (!posterVideo) return;

    const videoIntersection = (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.target) {
          const sourceTag = document.createElement('source');
          sourceTag.src = entry.target.dataset.src;
          sourceTag.type = "video/mp4";
          entry.target.appendChild(sourceTag);
          entry.target.src = entry.target.dataset.src;
          entry.target.removeAttribute("data-src");
          entry.target.play();
          this._posterObserver.disconnect();
          this._posterObserver = null;
        }
      }
    };
    this._posterObserver = new IntersectionObserver(videoIntersection);
    this._posterObserver.observe(posterVideo);
  }

  autoplayOnInit(element = this){
    const elementIntersection = (entries) => {
      if (!entries[0]?.isIntersecting) return;
      element.toggleView();
      if (this._autoplayObserver) {
        this._autoplayObserver.disconnect();
        this._autoplayObserver = null;
      }
    };
    this._autoplayObserver = new IntersectionObserver(elementIntersection, {
      root: null,
      rootMargin: '0px',
      threshold: [0, 0.01]
    });
    this._autoplayObserver.observe(element);
  }

  openModal(modal, opener){
    if(modal && opener != undefined) modal.show(opener);
  }
}
customElements.define("custom-video", CustomVideo);

/**
  * Open Modal javascript trigger
  *
  * */


/**
* Modal Dialogue
*
*/
class ModalDialog extends HTMLElement {
  constructor() {
    super();

    this.modal = this.querySelector('.modal');
    this.modalOpeners = [...document.querySelectorAll(`[data-modal-opener="${this.id}"]`)];
    this.closeBtn = this.querySelector('[id^="ModalClose-"]');
    this._boundHide = this.hide.bind(this);
    this._openerHandlers = [];
    this._onBackdropClick = null;
    this._onModalKeyup = null;
  }

  connectedCallback() {
    this._openerHandlers = [];
    this.modalOpeners.forEach((opener)=>{
      if(window.innerWidth < 575 && opener.getAttribute('data-modal-opener') != 'PopupModal-video'){
        const h = (evt) => {
          evt.preventDefault();
          this.show(opener);
        };
        opener.addEventListener('touchend', h);
        this._openerHandlers.push({ opener, type: 'touchend', h });
      } else {
        const h = (evt)=>{
          evt.preventDefault();
          let index_slide = parseInt(evt.target.closest(".ugc-video-wrap").getAttribute("data-index") - 1);
          this.show(opener);
          document.querySelector(".block__social-ugc-video-slider").swiper.slideTo(index_slide);
        };
        opener.addEventListener('click', h);
        this._openerHandlers.push({ opener, type: 'click', h });
      }
    });

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', this._boundHide);
    }

    this._onBackdropClick = (evt) => {
      if (evt.target === this) this.hide();
    };
    this._onModalKeyup = (evt) => {
      if (evt.code && evt.code.toUpperCase() === 'ESCAPE') this.hide();
    };
    this.addEventListener('click', this._onBackdropClick);
    this.addEventListener('keyup', this._onModalKeyup);
  }

  // Clean up event listeners
  disconnectedCallback() {
    this._openerHandlers.forEach(({ opener, type, h }) => {
      opener.removeEventListener(type, h);
    });
    this._openerHandlers = [];

    if (this.closeBtn) {
      this.closeBtn.removeEventListener('click', this._boundHide);
    }

    if (this._onBackdropClick) {
      this.removeEventListener('click', this._onBackdropClick);
    }
    if (this._onModalKeyup) {
      this.removeEventListener('keyup', this._onModalKeyup);
    }
  }

  /**
   * Open the Modal
   * @param {element} opener 
   */
  show(opener) {
    this.openedBy = opener;
    this.modal.classList.add('open');
    this.setAttribute('aria-hidden', false);

    SiteOverlay.prototype.showOverlay();
    Utility.trapFocus(this);
    Utility.forceFocus(this.closeBtn);
  }

  /**
   * Close Modal Dialogue
   */
  hide() {
    this.modal.classList.remove('open');
    this.setAttribute('aria-hidden', true);

    SiteOverlay.prototype.hideOverlay();
    Utility.removeTrapFocus(this.openedBy);
    if(this.id == 'PopupModal-video') this.querySelector('.video-container').innerHTML = '';
  }
}
customElements.define('modal-dialog', ModalDialog);

/**
   * Manage Custom Notification Trigger
   *
   */
class ToastNotification extends HTMLElement {
  constructor() {
    super();

    this.container = this.querySelector('.snotifyToast');
    this.closeBtn = this.querySelector('.snotifyToast__close');
    this.animateNotifcation = null;
    this._onCloseBtnClick = (e) => {
      e.preventDefault();
      this.hide();
    };

    // Set ARIA role for accessibility
    this.container.setAttribute('role', 'alert');
  }

  connectedCallback() {
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', this._onCloseBtnClick);
    }
  }

  disconnectedCallback() {
    if (this.closeBtn) {
      this.closeBtn.removeEventListener('click', this._onCloseBtnClick);
    }
  }

  /**
   * Notification Type
   * @param {string} title 
   * @param {string} body 
   * @param {object} options 
   */
  updateNotification(title, body, options){
    this.querySelector('.snotifyToast__title').textContent = title;
    this.querySelector('.snotifyToast__body').textContent = body;

    this.container.classList.add('open', `snotify-${options.type}`);
    this._manageToggle(options.timeout);
  }

  /**
   * Duration of notification to hide again
   * @param timeout 
   */
  _manageToggle(timeout) {
    if (this._toastRafId != null) {
      cancelAnimationFrame(this._toastRafId);
      this._toastRafId = null;
    }
    this.progressBar = this.querySelector('[data-toast-pr]');
    const totalFrames = Math.ceil(timeout / 16.67) / 2;
    let frame =0; 
    let width = 0;

    this.animateNotification = () => {
      width = (frame / totalFrames) * 100;
      this.progressBar.setAttribute('style', `width: ${width}%`);
      if (frame < totalFrames) {
        frame++;
        this._toastRafId = requestAnimationFrame(this.animateNotification);
      } else {
        this._toastRafId = null;
        this.closeBtn.dispatchEvent(new Event('click'));
      }
    };
    this._toastRafId = requestAnimationFrame(this.animateNotification);
  }

  hide() {
    if (this.animateNotifcation) {
      cancelAnimationFrame(this.animateNotifcation);
    }
    this.container.className = 'snotifyToast';
    [this.querySelector('.snotifyToast__title'),this.querySelector('.snotifyToast__body')].forEach(ele => ele.textContent = '');
    this.querySelector("[data-toast-pr]").removeAttribute("style");
  }
}
customElements.define("toast-notification", ToastNotification);
window.notificationEle = document.querySelector('toast-notification');
// notificationEle.updateNotification('Title', 'Body', {
//   type: 'success',
//   timeout: 5000
// });

class CustomDrawer extends HTMLElement {
  static _instances = [];
  static _docCaptureBound = false;
  static _onDocumentClickCapture = (event) => {
    const list = CustomDrawer._instances;
    for (let i = 0; i < list.length; i++) {
      const drawer = list[i];
      if (!drawer.isOpen) continue;
      if (!drawer.contains(event.target)) {
        drawer.closeDrawer();
      }
    }
  };
  static _ensureDocumentCapture() {
    if (CustomDrawer._docCaptureBound) return;
    document.addEventListener('click', CustomDrawer._onDocumentClickCapture, true);
    CustomDrawer._docCaptureBound = true;
  }

  constructor() {
      super();
      this.isOpen = false;

      this.openedBy = null;
      this.toggleDrawer = document.querySelectorAll(`[data-toggleDrawer="${this.id}"]`);
      this.closeButton = this.querySelector('[data-closedrawer]');
      this.forceFocus = this.querySelector('.drawer-header') || this.querySelector('.first-focusable-ele');
      this._toggleClickHandlers = [];
      this._boundCloseDrawer = this.closeDrawer.bind(this);
      this._boundKeyUp = this.onKeyUp.bind(this);
      this._boundFocusOut = this.focusOut.bind(this);
  }

  connectedCallback() {
    CustomDrawer._instances.push(this);
    CustomDrawer._ensureDocumentCapture();
    this.bindEvents();
  }

  disconnectedCallback() {
    const idx = CustomDrawer._instances.indexOf(this);
    if (idx > -1) CustomDrawer._instances.splice(idx, 1);

    this._toggleClickHandlers.forEach(({ btn, handler }) => {
      btn.removeEventListener('click', handler);
    });
    this._toggleClickHandlers = [];

    this.closeButton?.removeEventListener('click', this._boundCloseDrawer);
    this.removeEventListener('keyup', this._boundKeyUp);
    this.removeEventListener('focusout', this._boundFocusOut);
  }

  bindEvents(){
    this.toggleDrawer.forEach(btn => {
      btn.setAttribute('aria-expanded', false);
      const handler = (evt) => {
        evt.preventDefault();
        if(btn.dataset.for == 'collection-sorting'){
          this.classList.add('collection-sorting');
          setTimeout(() => {
            (this.isOpen) ? this.closeDrawer(evt) : this.openDrawer(evt.currentTarget);
          }, 500);
        }else{
          (this.isOpen) ? this.closeDrawer(evt) : this.openDrawer(evt.currentTarget);
        }
      };
      btn.addEventListener('click', handler);
      this._toggleClickHandlers.push({ btn, handler });
    });

    this.closeButton?.addEventListener('click', this._boundCloseDrawer);
    this.addEventListener('keyup', this._boundKeyUp);
    this.addEventListener('focusout', this._boundFocusOut);
  }

  /**
   * Escape Key Press to close drawer when focus is within the container
   */
  onKeyUp(evt) {
    if(evt.code && evt.code.toUpperCase() !== 'ESCAPE') return;
    this.closeDrawer();
  }

  /**
   * Focus Out Event to close drawer
   */
  focusOut(evt){
    if (!this.contains(evt.relatedTarget) && this.open) {
      this.closeDrawer();
    }
  }

  openDrawer(opener) {
    this.resetOtherDrawers();

    this.isOpen = true;
    this.openedBy = opener || null;
    this.classList.add('open');
    this.setAttribute('aria-hidden', 'false');
    this.openedBy?.setAttribute('aria-expanded', true);

    SiteOverlay.prototype.showOverlay();
    Utility.trapFocus(this, this.forceFocus);
    Utility.forceFocus(this.forceFocus);

    if (this.id === 'header-search-layer') {
      const rect = this.getBoundingClientRect();
      if (rect) {
        const topDistanceSearch = rect.top + rect.height;
        document.documentElement.style.setProperty('--top-distance-search', `${topDistanceSearch}px`);
      }
    }
  }

  closeDrawer() {
      this.isOpen = false;
      this.classList.remove('open');
      this.setAttribute('aria-hidden', 'true');
      this.openedBy?.setAttribute('aria-expanded', false);
      setTimeout(() => {
        this.classList.remove('collection-sorting');
      }, 500);
      
      SiteOverlay.prototype.hideOverlay();
      Utility.removeTrapFocus(this.openedBy);
      this.openedBy = null;
  }

  resetOtherDrawers(){
    const openDrawers = document.querySelectorAll('custom-drawer.open');
    for (let i = 0; i < openDrawers.length; i++) {
      openDrawers[i].closeDrawer();
    }
  }
}
customElements.define('custom-drawer', CustomDrawer);


(function(){
  function updateTopinRoot(){
    var headerEle = document.querySelector('.site-header');
    var topBar = document.querySelector('.topbar');
    if(headerEle && topBar){
      document.documentElement.style.setProperty('--bs-navdrawer', (headerEle.clientHeight + topBar.clientHeight) + 'px');
    }
  }
  function tryUpdate(){
    if(document.querySelector('.site-header')){ updateTopinRoot(); return true; }
    return false;
  }
  function schedule() {
    if (window.requestIdleCallback) requestIdleCallback(function(){ if(!tryUpdate()) requestAnimationFrame(tryUpdate); }, { timeout: 2000 });
    else setTimeout(function(){ if(!tryUpdate()) requestAnimationFrame(tryUpdate); }, 100);
  }
  if(document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule);
  else schedule();
})();

class AnnouncementBarStickySync {
  constructor() {
    this.root = document.documentElement;
    this.resizeObserver = null;
    this.syncAnnouncementBarHeight = this.syncAnnouncementBarHeight.bind(this);
  }

  randomizePromoIconDelays() {
    var promoBg = document.querySelector('.topbar--spring-promo .dispenser-bg');
    if (!promoBg) return;
    promoBg.querySelectorAll('.promo-banner-icon').forEach(function (icon) {
      icon.style.animationDelay = (Math.random() * 7).toFixed(2) + 's';
    });
  }

  getTopbar() {
    return document.querySelector('.announcement-bar .topbar');
  }

  syncAnnouncementBarHeight() {
    var topbar = this.getTopbar();
    var height = topbar ? Math.round(topbar.getBoundingClientRect().height) : 0;
    this.root.style.setProperty('--announcement-bar-height', height + 'px');
  }

  observeAnnouncementBar() {
    if (!window.ResizeObserver) return;
    var topbar = this.getTopbar();
    if (!topbar) return;
    if (this.resizeObserver) this.resizeObserver.disconnect();
    this.resizeObserver = new ResizeObserver(this.syncAnnouncementBarHeight);
    this.resizeObserver.observe(topbar);
  }

  init() {
    this.randomizePromoIconDelays();
    this.syncAnnouncementBarHeight();
    this.observeAnnouncementBar();
    window.addEventListener('load', this.syncAnnouncementBarHeight);
    window.addEventListener('resize', this.syncAnnouncementBarHeight);
  }
}

function bootAnnouncementBarStickySync() {
  var stickySync = new AnnouncementBarStickySync();
  stickySync.init();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootAnnouncementBarStickySync);
} else {
  bootAnnouncementBarStickySync();
}


const swiperSliders = document.querySelectorAll('swiper-container');
for (let s = 0; s < swiperSliders.length; s++) {
  const swiperEle = swiperSliders[s];
  swiperEle.addEventListener('swiperslidechange', (event) => {
    const index = event.detail[0].activeIndex;
    const swipDirection = event.detail[0].swipeDirection;
    const prevSlide = swiperEle.querySelector(`swiper-slide[data-index="${swipDirection == 'prev' ? index+2 : index}"]`);
    const activeSlide = swiperEle.querySelector(`swiper-slide[data-index="${index+1}"]`);

    // Pause previous slide video if available
    if(prevSlide && prevSlide.querySelector('.custom-video-container.playing--video')){
      let customElement = prevSlide.querySelector('custom-video');
      let videoEle = customElement.querySelector('video') || customElement.querySelector('iframe');
      if(videoEle && customElement.dataset.controls == 'true') {
        customElement._pauseVideo(videoEle);
      }
    }

    // Play active slide video if available
    if(prevSlide && activeSlide?.querySelector('.custom-video-container')){
      let customElement = activeSlide.querySelector('custom-video');
      if(customElement.videoStatus == 'empty'){
        customElement.toggleView();
      }else{
        let videoEle = customElement.querySelector('video') || customElement.querySelector('iframe');
        if(videoEle) customElement._playVideo(videoEle);
      }
    }
  });
}

document.querySelector('.megamenu')?.addEventListener('mouseenter', function() {
  const searchBox = document.querySelector('.header__search');
  const closeBtn = document.querySelector('.search-modal__close-button');

  if (searchBox?.classList.contains('open')) {
    closeBtn?.click();
  }
});

const modalwithButton = document.getElementById('PopupModal-video');
if (modalwithButton) {
  const videoContainer = modalwithButton.querySelector('#PopupModal-video .video-container');
  const closeBtn = modalwithButton.querySelector('#ModalClose-PopupModal-video');

  function openModal(videoSrc) {
    if (!videoSrc || !videoContainer) return;
    videoContainer.innerHTML = '';

  const isVimeo = /vimeo\.com/.test(videoSrc);
  if (isVimeo) {
    const iframe = document.createElement('iframe');
    iframe.src = videoSrc;
    iframe.className = 'w-100 h-100';
    iframe.setAttribute('title', 'Vimeo video player');
    videoContainer.appendChild(iframe);
  } else {
    const video = document.createElement('video');
    video.setAttribute('controls', '');
    video.setAttribute('autoplay', '');
    video.setAttribute('playsinline', '');
    video.src = videoSrc;
    const track = document.createElement('track');
    track.kind = 'captions';
    track.label = 'Captions';
    track.default = true;
    video.appendChild(track);
    videoContainer.appendChild(video);
  }
  }

  document.querySelectorAll('[data-modal-opener="PopupModal-video"]').forEach(button => {
    button.addEventListener('click', function () {
      const videoSrc = this.getAttribute('data-video-src');
      if (videoSrc) openModal(videoSrc);
    });
  });

  if (closeBtn) {
    closeBtn.addEventListener('click', function () {
      modalwithButton.style.display = 'none';
      if (videoContainer) videoContainer.innerHTML = '';
    });
  }

  window.addEventListener('click', function (event) {
    if (event.target === modalwithButton) {
      modalwithButton.style.display = 'none';
      if (videoContainer) videoContainer.innerHTML = '';
    }
  });
}

if(document.querySelector("[data-water-bowl-product]") != null){
  document.querySelector("[data-water-bowl-product] .accessories-swatch-input")?.addEventListener("change", function (){
    if(this.checked == true){
      let selected_color = document.querySelector('[data-water-bowl-product] .swatch input:checked').value;
      if(selected_color == "Essential Series - Black/White" || selected_color == "Black/White"){
        document.querySelector('[data-water-bowl-product] [data-optionindex="2"]').classList.add("d-none");
        const select = document.querySelector("[data-water-bowl-product]").querySelector('[data-optionindex="2"] [data-select-box]');
        if (select) {
          select.value = "Standard & Elevated"; // or the real option value
          select.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }else{
        document.querySelector('[data-water-bowl-product] [data-optionindex="2"]').classList.remove("d-none");
        if(this.currentVariant.option2 == "Standard & Elevated"){
          const select = document.querySelector("[data-water-bowl-product]").querySelector('[data-optionindex="2"] [data-select-box]');
          if (select) {
            select.value = "Standard"; // or the real option value
            select.dispatchEvent(new Event("change", { bubbles: true }));
          }
        }
        document.querySelector("[data-water-bowl-product]").querySelector('[data-optionindex="2"] option[data-option-value-handle="standard-elevated"]').classList.add("d-none");
      }
    }
  });
}


if(document.querySelector(".block__product-mobile-social-video") != null){
  document.querySelectorAll(".block__product-mobile-social-video .ugc-video-wrap").forEach((videoButton, index) => {
    videoButton.addEventListener("click", function (event) {
      event.preventDefault();
      var target = event.currentTarget;
      document.querySelector("#UGC-PopupModal-video .modal").classList.add("open");
      document.querySelector("site-overlay").classList.add("overlay--body");
      document.querySelector("body").classList.add("scroll-fixed");
      var index_slide = parseInt(target.getAttribute("data-index") - 1);
      document.querySelector(".block__social-ugc-video-slider").swiper.slideTo(index_slide);
    });
  });
}

(function initTestimonialScrollMute(){
  const testimonialRoot = document.querySelector(".customer-testimonial");
  if (!testimonialRoot || document.querySelectorAll(".testimonial-card").length === 0) return;

  let ticking = false;
  window.addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      const top = testimonialRoot.offsetTop - testimonialRoot.offsetHeight;
      const bottom = testimonialRoot.offsetTop + testimonialRoot.offsetHeight;
      if (window.scrollY < top || window.scrollY > bottom) {
        const soundOn = document.querySelectorAll(".testimonial-card .custom-video-sound-button.sound-on");
        for (let i = 0; i < soundOn.length; i++) {
          soundOn[i].click();
        }
      }
    });
  }, { passive: true });
})();


if(document.querySelector(".complete-feeding-bundle") != null){
  const bundle = document.querySelector(".complete-feeding-bundle");
  const checkboxes = bundle.querySelectorAll("[data-accessories-checkbox]");
  let isSyncingBundleCheckboxes = false;

  const syncBundleCheckboxesToOpenState = (shouldCheckAll) => {
    if (isSyncingBundleCheckboxes) return;
    isSyncingBundleCheckboxes = true;
    checkboxes.forEach((checkbox) => {
      const didValueChange = checkbox.checked !== shouldCheckAll;
      checkbox.checked = shouldCheckAll;
      if (didValueChange) {
        checkbox.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });
    isSyncingBundleCheckboxes = false;
  };

  bundle.addEventListener(
    "click",
    (event) => {
      if (!bundle.contains(event.target)) return;
      if (event.target.closest("[data-qty-container]")) return;

      const link = event.target.closest("a[href]");
      if (link) {
        const href = link.getAttribute("href")?.trim() || "";
        if (href && href !== "#" && !href.startsWith("#")) return;
      }

      const label = event.target.closest("label");
      const activatesBundleCheckbox =
        event.target.closest("[data-accessories-checkbox]") ||
        (label &&
          bundle.contains(label) &&
          (label.querySelector("[data-accessories-checkbox]") ||
            (label.htmlFor &&
              bundle
                .querySelector(`#${CSS.escape(label.htmlFor)}`)
                ?.hasAttribute("data-accessories-checkbox"))));

      if (activatesBundleCheckbox) {
        event.preventDefault();
      }

      const hadOpen = bundle.classList.contains("open");
      const isOpen = bundle.classList.toggle("open");
      syncBundleCheckboxesToOpenState(isOpen);
      if (!hadOpen && isOpen) {
        bundle
          .querySelectorAll('[data-for="decrease"]')
          .forEach((btn) => btn.classList.remove("disabled"));
        bundle.querySelectorAll('input[name="quantity"]').forEach((input) => {
          input.value = 1;
          input.dispatchEvent(new Event("input", { bubbles: true }));
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }
    },
    true,
  );
}